import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  signalStoreFeature,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import {
  setFulfilled,
  setPending,
  setError,
  withRequestStatus,
  withSyncToWebStorage,
} from '@abook-workspace/abook-library/store-features';
import { firstValueFrom, forkJoin, Observable } from 'rxjs';
import { CheckoutService } from './checkout.service';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { apiErrorMessage } from '@core/services/api-error-message';
import {
  Booking,
  BookingRequest,
  BookingStatus,
  BookingMode,
  BookingTypeMode,
  CheckoutErrorType,
  CheckoutPeriodInput,
  CustomerJourneyType,
  PaymentType,
} from './models/checkout.model';

const BOOKING_FAILED = 'Bookingen kunne ikke gennemføres.';

interface CheckoutState {
  bookingId: number | null;
  booking: Booking | null;
  bookingRequest: BookingRequest | null;
  errorType: CheckoutErrorType | null;
  /** Årsagen fra API'et, når den er mere præcis end en flowtilstand — se apiErrorMessage. */
  errorMessage: string | null;
  paymentInstrument: string | null;
  bookingMode: BookingMode | null;
}

export const CheckoutStore = signalStore(
  { providedIn: 'root' },

  signalStoreFeature(
    withState<CheckoutState>({
      bookingId: null,
      booking: null,
      bookingRequest: null,
      errorType: null,
      errorMessage: null,
      paymentInstrument: null,
      bookingMode: null,
    }),
  ),
  withRequestStatus(),

  withSyncToWebStorage({
    key: 'abook:checkout-store',
    type: 'session',
    restoreOnInit: true,
    saveStateChangesAfterMs: 300,
  }),

  withComputed(({ booking }) => ({
    itemsCount: computed(() => booking()?.bookingPeriods?.length ?? 0),

    total: computed(() =>
      (booking()?.bookingPeriods ?? []).reduce((sum, bp) => {
        const base = bp.price ?? 0;
        // CR-002: guard against null price/amount from unexpected API responses
        const addons = (bp.bookingPeriodEntries ?? []).reduce(
          (s, e) => s + (e.price ?? 0) * (e.amount ?? 0),
          0,
        );
        return sum + base + addons;
      }, 0),
    ),

    // Hero display values — derived here so pages don't duplicate the accessor chain
    heroEyebrow: computed(() => booking()?.bookingPeriods[0]?.resource?.parent?.name ?? ''),
    heroTitle: computed(() => booking()?.bookingPeriods[0]?.activity?.name ?? ''),
  })),

  withMethods((store) => {
    const checkoutService = inject(CheckoutService);
    const authService = inject(AuthenticationService);

    return {
      setBookingId(id: number): void {
        patchState(store, { bookingId: id });
      },

      setErrorType(errorType: CheckoutErrorType | null): void {
        patchState(store, { errorType });
      },

      setPaymentInstrument(instrument: string | null): void {
        patchState(store, { paymentInstrument: instrument });
      },

      async getBooking(id: number): Promise<boolean> {
        patchState(store, setPending());
        try {
          const booking = await firstValueFrom(checkoutService.getBooking(id));
          patchState(store, { booking }, setFulfilled());
          return true;
        } catch {
          patchState(store, { booking: null, bookingId: null }, setError('booking_not_found'));
          return false;
        }
      },

      async updateBooking(id: number, bookingRequest: BookingRequest): Promise<void> {
        patchState(store, { bookingRequest }, setPending());
        const updated = await firstValueFrom(checkoutService.updateBooking(id, bookingRequest));
        // PUT /bookings/{id} returns a non-expanded booking — periods lack resource/activity.
        // Deliberately merge: keep the existing expanded bookingPeriods so the hero and sidebar
        // stay populated. Booking.totalPrice is NOT used for the displayed total — total() derives
        // it from periods + bookingPeriodEntries so it always includes addon costs correctly.
        const existingPeriods = store.booking()?.bookingPeriods;
        const booking = existingPeriods ? { ...updated, bookingPeriods: existingPeriods } : updated;
        patchState(store, { booking }, setFulfilled());
      },

      async verifyBooking(id: number): Promise<boolean> {
        try {
          await firstValueFrom(checkoutService.verifyBooking(id, store.bookingMode()));
          return true;
        } catch {
          patchState(store, { errorType: CheckoutErrorType.bookingInvalidPrices });
          return false;
        }
      },

      async completeBooking(bookingId: number): Promise<boolean> {
        patchState(store, setPending());
        try {
          await firstValueFrom(checkoutService.completeBooking(bookingId));
          const refreshed = await firstValueFrom(checkoutService.getBooking(bookingId));
          patchState(store, { booking: refreshed }, setFulfilled());
          return true;
        } catch (error) {
          const message = apiErrorMessage(error, BOOKING_FAILED);
          patchState(store, { errorMessage: message }, setError(message));
          return false;
        } finally {
          patchState(store, { bookingId: null });
        }
      },

      async submitBookingApproval(bookingId: number): Promise<boolean> {
        patchState(store, setPending());
        try {
          await firstValueFrom(checkoutService.submitBookingApproval(bookingId));
          const refreshed = await firstValueFrom(checkoutService.getBooking(bookingId));
          patchState(store, { booking: refreshed }, setFulfilled());
          return true;
        } catch (error) {
          const message = apiErrorMessage(error, BOOKING_FAILED);
          patchState(store, {
            errorType: CheckoutErrorType.submitApprovalFailed,
            errorMessage: message,
            ...setError(message),
          });
          return false;
        } finally {
          patchState(store, { bookingId: null });
        }
      },

      async getBookingByUid(uid: string): Promise<Booking | null> {
        try {
          const booking = await firstValueFrom(checkoutService.getBookingByUid(uid));
          patchState(store, { booking });
          return booking;
        } catch {
          // non-fatal: complete page falls back to whatever is already in the store
          return null;
        }
      },

      clearBooking(bookingId: number): Observable<unknown> {
        patchState(store, {
          bookingId: null,
          booking: null,
          bookingRequest: null,
          errorType: null,
          errorMessage: null,
          paymentInstrument: null,
        });
        return checkoutService.deleteBooking(bookingId);
      },

      async initCheckout(
        periods: CheckoutPeriodInput[],
        _journeyType: CustomerJourneyType,
        bookingMode?: BookingMode | null,
      ): Promise<void> {
        patchState(store, { errorMessage: null }, setPending());
        try {
          patchState(store, { bookingMode: bookingMode ?? null });

          const user = authService.user();
          const userId = user?.id;
          const bookingRequest: BookingRequest = {
            userId,
            paymentType: PaymentType.Unknown,
            note: null,
            externalNote: null,
            pspElement: null,
          };
          const booking = await firstValueFrom(checkoutService.createBooking(bookingRequest));
          patchState(store, { bookingId: booking.id, bookingRequest });

          await firstValueFrom(
            forkJoin(
              periods.map((period) =>
                checkoutService.addBookingPeriod({
                  bookingId: booking.id,
                  resourceId: period.resourceId,
                  bookingTypeId: BookingTypeMode.BOOKING,
                  // The info-screen text, and the administration's "Navn - infoskærm" — read off
                  // the day's bookings, the booking dialog and the info screens themselves.
                  //
                  // What belongs here is customer.DisplayName, carried on the token as display_name.
                  // A customer who has never set one gets an empty claim, so the booker's own Name
                  // stands in — a stand-in rather than nothing, because the field that would
                  // otherwise supply it is not shown to every tenant and need not be touched by the
                  // booker (#474).
                  note: user?.displayName || user?.name || null,
                  price: period.price,
                  statusId: BookingStatus.New,
                  start: period.start,
                  end: period.end,
                  activityId: period.activityId,
                }),
              ),
            ),
          );

          const fullBooking = await firstValueFrom(checkoutService.getBooking(booking.id));
          patchState(store, { booking: fullBooking }, setFulfilled());
        } catch (error) {
          const message = apiErrorMessage(error, BOOKING_FAILED);
          patchState(store, { errorMessage: message }, setError(message));
        }
      },
    };
  }),
);
